import { Injectable, inject } from '@angular/core';
import { DataService } from './data.service';
import { AuthService } from '../auth/auth.service';
import { ClientRelationship, ActivityAction, ActorType, ClientActivityLog } from '../models/relationship.model';
import { FUNCTIONS, getFunctionName } from '../firebase.config';
import { httpsCallable } from 'firebase/functions';

export type PermissionAction = 'view' | 'commit' | 'download_documents' | 'edit_profile' | 'view_financials';

export interface PermissionResult {
    allowed: boolean;
    reason?: string;
    relationship?: ClientRelationship;
}

@Injectable({
    providedIn: 'root',
})
export class PermissionsService {
    private dataService = inject(DataService);
    private authService = inject(AuthService);
    private readonly logClientActivityCallable = httpsCallable<{
        clientId: string;
        action: ActivityAction;
        details: Record<string, any>;
        dealId?: string;
        commitmentId?: string;
        relationshipId?: string;
    }, { success: boolean }>(
        FUNCTIONS,
        getFunctionName('logClientActivity')
    );

    /**
     * Check if a user can perform an action on behalf of or for a target client
     */
    async canPerformAction(
        userId: string,
        targetClientId: string,
        action: PermissionAction
    ): Promise<PermissionResult> {
        // 1. Check if acting on own behalf
        if (userId === targetClientId) {
            return { allowed: true, reason: 'Own account' };
        }

        // 2. Check if Admin
        const user = this.authService.userProfile();
        if (user?.role === 'Admin') {
            return { allowed: true, reason: 'Admin access' };
        }

        // 3. Check relationships
        const relationship = await this.getActiveRelationship(userId, targetClientId);
        if (!relationship) {
            return { allowed: false, reason: 'No active relationship found' };
        }

        // 4. Check specific permissions
        switch (action) {
            case 'commit':
                return {
                    allowed: relationship.canCommit,
                    reason: relationship.canCommit ? 'Trustee can commit' : 'Insufficient permissions to commit',
                    relationship
                };

            case 'download_documents':
                return {
                    allowed: relationship.canDownloadDocuments,
                    reason: relationship.canDownloadDocuments ? 'Can download documents' : 'Document download not permitted',
                    relationship
                };

            case 'view':
            case 'view_financials':
                return {
                    allowed: relationship.canViewFinancials,
                    reason: relationship.canViewFinancials ? 'Can view financials' : 'Financial view not permitted',
                    relationship
                };

            case 'edit_profile':
                // Only admin or self can edit profile
                return {
                    allowed: false,
                    reason: 'Only client or admin can edit profile',
                    relationship
                };

            default:
                return { allowed: false, reason: 'Unknown action' };
        }
    }

    /**
     * Get active relationship between user and client
     * FIXED: Match database convention where primaryClientId=trustee, associatedUserId=client
     */
    async getActiveRelationship(userId: string, clientId: string): Promise<ClientRelationship | null> {
        try {
            const canonical = await this.dataService.query<ClientRelationship>('clientRelationships', [
                { field: 'primaryClientId', operator: '==', value: clientId },
                { field: 'associatedUserId', operator: '==', value: userId },
                { field: 'isActive', operator: '==', value: true }
            ]);

            if (canonical.length > 0) {
                return canonical[0];
            }

            const legacy = await this.dataService.query<ClientRelationship>('clientRelationships', [
                { field: 'primaryClientId', operator: '==', value: userId },
                { field: 'associatedUserId', operator: '==', value: clientId },
                { field: 'isActive', operator: '==', value: true }
            ]);

            if (legacy.length === 0) {
                return null;
            }

            const relationship = legacy[0];
            return {
                ...relationship,
                primaryClientId: clientId,
                primaryClientName: relationship.associatedUserName,
                associatedUserId: userId,
                associatedUserName: relationship.primaryClientName,
            };
        } catch (error) {
            console.error('Error fetching relationship:', error);
            return null;
        }
    }

    /**
     * Log an activity performed by a user on behalf of a client
     */
    async logActivity(
        clientId: string,
        action: ActivityAction,
        details: Record<string, any>,
        dealId?: string,
        commitmentId?: string,
        relationshipId?: string
    ): Promise<void> {
        const actor = this.authService.userProfile();
        if (!actor) {
            console.error('Cannot log activity: No authenticated user');
            return;
        }

        let actorType: ActorType = 'Self';
        if (actor.uid !== clientId) {
            if (actor.role === 'Admin') {
                actorType = 'Admin';
            } else if (actor.subRole === 'Trustee') {
                actorType = 'Trustee';
            } else if (actor.subRole === 'ReadOnly') {
                actorType = 'ReadOnly';
            }
        }

        const log: Omit<ClientActivityLog, 'id'> = {
            timestamp: new Date().toISOString(),
            clientId,
            actorId: actor.uid,
            actorName: actor.displayName,
            actorType,
            action,
            details,
            ...(dealId && { dealId }),
            ...(commitmentId && { commitmentId }),
            ...(relationshipId && { relationshipId }),
        };

        try {
            await this.logClientActivityCallable({
                clientId: log.clientId,
                action: log.action,
                details: log.details,
                ...(log.dealId && { dealId: log.dealId }),
                ...(log.commitmentId && { commitmentId: log.commitmentId }),
                ...(log.relationshipId && { relationshipId: log.relationshipId }),
            });
        } catch (error) {
            console.error('Error logging activity:', error);
            // Don't throw - logging failure shouldn't break the main flow
        }
    }

    /**
     * Get activity logs for a client
     */
    async getActivityLogs(clientId: string, limit: number = 50): Promise<ClientActivityLog[]> {
        try {
            const logs = await this.dataService.query<ClientActivityLog>('clientActivityLog', [
                { field: 'clientId', operator: '==', value: clientId }
            ]);

            return logs
                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                .slice(0, limit);
        } catch (error) {
            console.error('Error fetching activity logs:', error);
            return [];
        }
    }

    /**
     * Check if user can access a specific account of a client
     */
    canAccessAccount(relationship: ClientRelationship, accountId: string): boolean {
        // If no account restrictions, allow access to all accounts
        if (!relationship.allowedAccounts || relationship.allowedAccounts.length === 0) {
            return true;
        }

        // Check if account is in allowed list
        return relationship.allowedAccounts.includes(accountId);
    }
}
